import icons from '@/assets/icons';
import { QuestionnaireStatus, SectionType } from '@/components/decision-tree';
import {
  MultipleChoiceQuestion,
  NumericQuestion,
  SingleChoiceQuestion,
  WeightAnswer,
  WeightQuestion,
} from '@/components/decision-tree/components/SurveyItem/components/Question/questionTypes';
import { displayDateFormat } from '@/constants/dates';
import { useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { useDisclosure } from '@cmpsr/components';
import { format } from 'date-fns';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import EditAnswerButton from '@/components/modal-edit-answer/edit-answer-button/edit-answer-button';
import ModalEditAnswer from '@/components/modal-edit-answer/modal-edit-answer';
import { Fragment, useState, useMemo, useCallback, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import RiskAssessmentStatusBadge from '../risk-assessment-status-badge/risk-assessment-status-badge';
import { ChevronDownAnimated } from '@/components/icons/chevron-down-animated';

type RiskAssessmentSectionsProps = {
  questionnaireStatus: QuestionnaireStatus;
};

type SectionData = {
  surveyItemId: string;
  label: string;
  answerText: string | undefined;
  answerDate: string;
  isIDontKnowAnswer: boolean;
};

const isSectionData = (data: SectionData | undefined): data is SectionData => data !== undefined;

type SectionAction = {
  type: 'TOGGLE_SECTION';
  index: number;
};

const sectionReducer = (state: { [key: number]: boolean }, action: SectionAction) => {
  switch (action.type) {
    case 'TOGGLE_SECTION':
      return {
        ...state,
        [action.index]: !state[action.index]
      };
    default:
      return state;
  }
};

export default function RiskAssessmentSections({ questionnaireStatus }: RiskAssessmentSectionsProps) {
  const {
    isOpen: showModalEditAnswer,
    onOpen: onShowModalEditAnswer,
    onClose: onHideModalEditAnswer,
  } = useDisclosure();
  const { data: questionnaireData } = useGetQuestionnaire();
  const riskAssessmentSections = questionnaireData?.questionnaire?.sections;
  const riskAssessmentAnswers = questionnaireData?.answers;
  const [searchParams, setSearchParams] = useSearchParams();

  const [openSections, dispatch] = useReducer(sectionReducer, {});
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);

  // TODO: change when integrating new questionnaire w/ page
  const getSectionUnclearAnswers = useCallback((sectionId: string) => {
    const sectionIDKAnswers = riskAssessmentAnswers?.filter(
      (answer) => answer.sectionId === sectionId && answer.answer?.type === 'iDontKnow'
    );
    return sectionIDKAnswers?.length ?? 0;
  }, [riskAssessmentAnswers]);

  const getSectionData = useCallback((section: SectionType): Array<SectionData> => {
    return section.surveyItems
      .map((surveyItem) => {
        if (surveyItem.surveyItemType === 'interstitial') return undefined;

        const answer = riskAssessmentAnswers?.find(
          (answer) => answer.surveyItemId === surveyItem.id && answer.sectionId === section.id
        );

        if (!answer) return undefined;

        let answerText;
        let isIDontKnowAnswer = false;

        switch (answer?.answer?.type) {
          case 'singleChoice':
            {
              const { value } = answer.answer;
              const singleChoiceQuestion = surveyItem as SingleChoiceQuestion;
              const singleChoiceOption = singleChoiceQuestion.options.find((option) => option.id === value);
              answerText = singleChoiceOption?.label;
            }
            break;
          case 'multipleChoice':
            {
              const { values } = answer.answer;
              const multipleChoiceQuestion = surveyItem as MultipleChoiceQuestion;
              const selectedOptions = values.map((value) => {
                const option = multipleChoiceQuestion.options.find((option) => option.id === value);
                return option?.label;
              });

              const noneOption = multipleChoiceQuestion.options.find((option) => option.type === 'none');
              if (selectedOptions.length === 0 && noneOption) {
                selectedOptions.push(noneOption.label);
              }

              answerText = selectedOptions.join(', ');
            }
            break;
          case 'number':
            {
              const numericQuestion = surveyItem as NumericQuestion;
              answerText = `${answer.answer.value} ${numericQuestion.trailingMask}`;
            }
            break;
          case 'height':
            answerText = `${answer.answer.feet}ft ${answer.answer.inches}in`;
            break;
          case 'date':
            answerText = answer.answer.value;
            break;
          case 'weight':
            {
              const weightQuestion = surveyItem as WeightQuestion;
              const { pounds, ounces } = answer.answer as WeightAnswer;
              const poundsText = `${pounds} ${weightQuestion.pounds.trailingMask}`;
              const ouncesText = `${ounces} ${weightQuestion.ounces.trailingMask}`;
              answerText = `${poundsText} ${ouncesText}`;
            }
            break;
          case 'iDontKnow':
            answerText = "I'm not sure right now";
            isIDontKnowAnswer = true;
            break;
        }

        const answerDate = answer?.answeredAt !== undefined ? format(answer.answeredAt, displayDateFormat) : '';

        return {
          surveyItemId: surveyItem.id,
          label: surveyItem.label,
          answerText,
          answerDate,
          isIDontKnowAnswer,
        };
      })
      .filter(isSectionData);
  }, [riskAssessmentAnswers]);

  const getBadgeStatus = (unclearAnswers: number | undefined): 'complete' | 'inaccurate' | undefined => {
    if (unclearAnswers === undefined) return;

    return unclearAnswers === 0 ? 'complete' : 'inaccurate';
  };

  const getBadgeText = (unclearAnswers: number | undefined): string | undefined => {
    if (unclearAnswers === undefined) return;

    return unclearAnswers === 0 ? '100% complete' : `${unclearAnswers} unclear answers`;
  };

  const handleEditClick = (surveyItemId: string) => {
    setSelectedQuestionId(surveyItemId);
    onShowModalEditAnswer();
  };

  const handleCloseModal = () => {
    setSelectedQuestionId(null);
    searchParams.delete('surveyItemId');
    setSearchParams(searchParams);
    onHideModalEditAnswer();
  };

  const toggleSection = useCallback((index: number) => {
    dispatch({ type: 'TOGGLE_SECTION', index });
  }, []);

  const sectionsWithData = useMemo(() => {
    const sectionIcons = ['demographics', 'Diet-General', 'occupation-alt', 'Clinical-Dr', 'biomarkers-alt-1'];
    
    return riskAssessmentSections?.map((section, index) => {
      const unclearAnswers = getSectionUnclearAnswers(section.id);
      const questionsData = getSectionData(section);
      const badgeStatus = getBadgeStatus(unclearAnswers);
      const badgeText = getBadgeText(unclearAnswers);
      const icon = sectionIcons[index] as keyof typeof icons;
      const isOpen = openSections[index] || false;

      return {
        section,
        index,
        unclearAnswers,
        questionsData,
        badgeStatus,
        badgeText,
        icon,
        isOpen,
      };
    });
  }, [riskAssessmentSections, getSectionData, getSectionUnclearAnswers, openSections]);

  return (
    <>
      {questionnaireStatus !== QuestionnaireStatus.uncompleted && (
        <div className="dashboard-reset flex flex-col flex-grow gap-4 py-5 max-w-full sm:px-6 sm:gap-6">
          {sectionsWithData?.map(({ section, index, questionsData, badgeStatus, badgeText, icon, isOpen }) => (
            <div
              key={index}
              className="bg-input rounded-4 p-5 w-full max-w-[343px] sm:max-w-none sm:p-6 mx-auto"
            >
                <button
                  onClick={() => toggleSection(index)}
                  className={`w-full p-0 ${isOpen ? 'mb-4 sm:mb-6' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-light flex items-center justify-center rounded-[6px] border border-[color:var(--color-ui-element-divider)] w-10 h-10 shrink-0">
                      <IconCatch icon={icon} backgroundColor="transparent" width="28.5px" height="28.5px" />
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center flex-grow gap-2 sm:gap-4 min-w-0">
                      <div className="flex items-center w-[200px] sm:w-[340px]">
                        <h3 className="text-primary font-[family-name:var(--font-family-libre-baskerville)] italic text-[20px] leading-[28px] tracking-[1px] sm:text-[25px] sm:leading-[38px] sm:tracking-[0.5px] text-left">
                          {section.name}
                        </h3>
                      </div>
                      {badgeStatus && (
                        <div className="inline-flex shrink-0">
                          <RiskAssessmentStatusBadge status={badgeStatus}>{badgeText}</RiskAssessmentStatusBadge>
                        </div>
                      )}
                    </div>
                    <div className="ml-auto shrink-0">
                    <ChevronDownAnimated isOpen={isOpen} />
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="w-full mt-4 sm:mt-6">
                    {questionsData.map(({ surveyItemId, label, answerText, answerDate, isIDontKnowAnswer }, qIndex) => (
                      <Fragment key={surveyItemId}>
                        <div className={`flex gap-3 sm:gap-4 py-3 border-[color:var(--color-ui-element-divider)] ${qIndex === 0 ? 'border-t' : ''} border-b`}>
                          <div className="flex flex-grow gap-3 min-w-0">
                            {isIDontKnowAnswer && (
                              <div className="bg-[color:var(--color-catch-yellow)] rounded-[6px] w-8 h-8 flex items-center justify-center shrink-0">
                                <svg
                                  className="w-5 h-5 text-[color:var(--color-catch-yellow-dark)]"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                            <div className="flex flex-col flex-grow gap-1 min-w-0">
                              <div className="text-primary font-[family-name:var(--font-family-inter)] font-medium text-[16px] leading-[24px] tracking-[0.2px]">
                                <MarkdownTextTailwind>{label}</MarkdownTextTailwind>
                              </div>
                              {answerText && (
                                <p className="text-secondary font-[family-name:var(--font-family-inter)] font-normal text-[14px] leading-[20px]">
                                  {answerText}
                                </p>
                              )}
                              {answerDate && (
                                <p className="sm:hidden font-[family-name:var(--font-family-inter)] font-normal text-[12px] leading-[18px] tracking-[0.2px] text-[#b0b0b0]">
                                  Last update {answerDate}
                                </p>
                              )}
                            </div>
                          </div>
                          {answerDate && (
                            <div className="hidden sm:flex sm:flex-col sm:items-center sm:justify-center sm:text-center whitespace-pre font-[family-name:var(--font-family-inter)] font-normal text-xs leading-[18px] tracking-[0.2px] text-[#b0b0b0]">
                              <p className="mb-0">Last update</p>
                              <p>{answerDate}</p>
                            </div>
                          )}
                          <div className="flex items-center">
                            <div className="border-l border-[color:var(--color-ui-element-divider)] pl-4 h-full flex items-center">
                              <EditAnswerButton onClick={() => handleEditClick(surveyItemId)} />
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
      {showModalEditAnswer && (
        <ModalEditAnswer isOpen onCloseClick={handleCloseModal} surveyItemId={selectedQuestionId} />
      )}
    </>
  );
}
